import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Building2, UserPlus, Users, Shield, Crown, Eye, Trash2, Loader2, Sparkles, CheckCircle2 } from 'lucide-react';
import { useAuthStore } from '../../state/authStore';
import PageHeader from '../../components/layout/PageHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/orgs`;
const PENDING_ORG_KEY = 'yenom-pending-org';

const SEBI_CATEGORIES = [
  { value: 'RIA', label: 'Investment Adviser (RIA)' },
  { value: 'RA',  label: 'Research Analyst (RA)' },
  { value: 'PMS', label: 'Portfolio Manager (PMS)' },
  { value: 'AMC', label: 'Asset Management Company (AMC)' },
  { value: 'DEP', label: 'Distributor (MFD / AMFI)' },
  { value: 'SEBI_OTHER', label: 'Other SEBI-registered intermediary' },
];

function defaultDisclaimer(regNumber, sebiCategory, orgName) {
  const cat = SEBI_CATEGORIES.find((c) => c.value === sebiCategory)?.label || 'SEBI-registered intermediary';
  return (
    `${orgName || 'Our firm'} is a ${cat} (SEBI registration: ${regNumber || '—'}). ` +
    `Information in this document is prepared solely for the named client and is not an ` +
    `offer or solicitation. Past performance is not indicative of future results. ` +
    `Investments in securities are subject to market risk. Please read all offer documents carefully.`
  );
}

function RoleIcon({ role }) {
  if (role === 'owner') return <Crown className="w-3 h-3 text-amber-500" />;
  if (role === 'admin') return <Shield className="w-3 h-3 text-blue-500" />;
  if (role === 'viewer') return <Eye className="w-3 h-3 text-muted-foreground" />;
  return <Users className="w-3 h-3 text-emerald-500" />;
}

function RoleBadge({ role }) {
  const colors = {
    owner: 'bg-amber-500/10 text-amber-600',
    admin: 'bg-blue-500/10 text-blue-600',
    member: 'bg-emerald-500/10 text-emerald-600',
    viewer: 'bg-gray-500/10 text-gray-600',
  };
  return (
    <Badge className={`${colors[role] || colors.member} text-[10px]`}>
      <RoleIcon role={role} />
      <span className="ml-1 capitalize">{role}</span>
    </Badge>
  );
}

// Read pending institutional org details stashed by SignupPage.
function readPendingOrg() {
  try {
    const raw = localStorage.getItem(PENDING_ORG_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function clearPendingOrg() {
  try { localStorage.removeItem(PENDING_ORG_KEY); } catch { /* noop */ }
}

export default function OrgSettingsPage() {
  const user = useAuthStore(s => s.user);
  const userId = user?.id;
  const userType = user?.userType;
  const isInstitutional = userType === 'institutional';

  const location = useLocation();
  const navigate = useNavigate();
  const onboardingParam = useMemo(() => new URLSearchParams(location.search).get('onboarding') === '1', [location.search]);

  const [orgs, setOrgs] = useState([]);
  const [activeOrg, setActiveOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [creating, setCreating] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [brandForm, setBrandForm] = useState({
    logo_url: '',
    registration_number: '',
    sebi_category: 'RIA',
    disclaimer_text: '',
    primary_color: '#10b981',
  });
  const [brandDirty, setBrandDirty] = useState(false);
  const [brandSaving, setBrandSaving] = useState(false);
  const [brandSaved, setBrandSaved] = useState(false);
  const [audit, setAudit] = useState({ items: [], total: 0 });

  const loadOrgs = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(API, { params: { user_id: userId } });
      setOrgs(data || []);
      if (data?.length > 0 && !activeOrg) {
        loadOrgDetail(data[0].id);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [userId]);

  const loadOrgDetail = async (orgId) => {
    try {
      const { data } = await axios.get(`${API}/${orgId}`);
      setActiveOrg(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  // Onboarding: if retail flag is off, pending-org data from signup is present,
  // and user has no orgs yet — prefill the create form.
  useEffect(() => {
    if (!isInstitutional) return;
    if (loading || orgs.length > 0) return;
    const pending = readPendingOrg();
    if (pending?.name) {
      setNewOrgName(pending.name);
      setShowCreate(true);
      setBrandForm((f) => ({
        ...f,
        registration_number: pending.sebi_registration_number || f.registration_number,
        sebi_category: pending.sebi_category || f.sebi_category,
        disclaimer_text: defaultDisclaimer(
          pending.sebi_registration_number,
          pending.sebi_category,
          pending.name,
        ),
      }));
    }
  }, [isInstitutional, loading, orgs.length]);

  const handleCreate = async () => {
    if (!newOrgName.trim()) return;
    setCreating(true);
    try {
      const pending = readPendingOrg();
      const { data } = await axios.post(API, {
        name: newOrgName.trim(),
        user_id: userId,
        // Backend may or may not accept these yet; forwarded for forward-compat.
        sebi_category: pending?.sebi_category,
        sebi_registration_number: pending?.sebi_registration_number,
        registered_address: pending?.registered_address,
        compliance_officer_email: pending?.compliance_officer_email,
      });
      setNewOrgName('');
      setShowCreate(false);
      await loadOrgs();
      loadOrgDetail(data.id);
      // Persist SEBI fields as branding once the org exists.
      if (pending && data?.id) {
        try {
          await axios.put(`${API}/${data.id}/branding`, {
            logo_url: '',
            registration_number: pending.sebi_registration_number || '',
            disclaimer_text: defaultDisclaimer(
              pending.sebi_registration_number,
              pending.sebi_category,
              pending.name,
            ),
            primary_color: '#10b981',
          });
        } catch { /* noop */ }
        clearPendingOrg();
      }
    } catch { /* ignore */ }
    setCreating(false);
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !activeOrg) return;
    setInviting(true);
    try {
      await axios.post(`${API}/${activeOrg.id}/invite`, {
        email: inviteEmail.trim(), user_id: userId, role: inviteRole,
      });
      setInviteEmail('');
      setShowInvite(false);
      loadOrgDetail(activeOrg.id);
    } catch { /* ignore */ }
    setInviting(false);
  };

  const handleRemove = async (memberId) => {
    if (!activeOrg) return;
    try {
      await axios.delete(`${API}/${activeOrg.id}/members/${memberId}`);
      loadOrgDetail(activeOrg.id);
    } catch { /* ignore */ }
  };

  const handleRoleChange = async (memberId, newRole) => {
    if (!activeOrg) return;
    try {
      await axios.patch(`${API}/${activeOrg.id}/members/${memberId}`, { role: newRole });
      loadOrgDetail(activeOrg.id);
    } catch { /* ignore */ }
  };

  const myRole = activeOrg?.members?.find(m => m.user_id === userId)?.role;
  const canManage = myRole === 'owner' || myRole === 'admin';

  useEffect(() => {
    if (!activeOrg?.id) return;
    setBrandForm({
      logo_url: activeOrg.logo_url || '',
      registration_number: activeOrg.registration_number || '',
      sebi_category: activeOrg.sebi_category || 'RIA',
      disclaimer_text: activeOrg.disclaimer_text || defaultDisclaimer(
        activeOrg.registration_number,
        activeOrg.sebi_category,
        activeOrg.name,
      ),
      primary_color: activeOrg.primary_color || '#10b981',
    });
    setBrandDirty(false);
    setBrandSaved(false);
    axios.get(`${API}/${activeOrg.id}/audit-log`).then(({ data }) => {
      setAudit({ items: data.items || [], total: data.total || 0 });
    }).catch(() => setAudit({ items: [], total: 0 }));
  }, [activeOrg?.id]);

  const updateBrand = (patch) => {
    setBrandForm((f) => ({ ...f, ...patch }));
    setBrandDirty(true);
    setBrandSaved(false);
  };

  const saveBrand = async () => {
    if (!activeOrg?.id) return;
    setBrandSaving(true);
    try {
      await axios.put(`${API}/${activeOrg.id}/branding`, brandForm);
      setBrandDirty(false);
      setBrandSaved(true);
      loadOrgDetail(activeOrg.id);
    } catch { /* ignore */ }
    setBrandSaving(false);
  };

  const needsRegNumber = isInstitutional && activeOrg && !brandForm.registration_number;
  const needsDisclaimer = isInstitutional && activeOrg && !brandForm.disclaimer_text;
  const onboardingIncomplete = needsRegNumber || needsDisclaimer;

  return (
    <div className="space-y-4 max-w-3xl">
      <PageHeader
        title={isInstitutional ? 'Firm settings' : 'Organization'}
        description={isInstitutional
          ? 'Your firm details, branding, and team. Your SEBI registration appears on every client output.'
          : 'Manage your team and organization settings.'}
      />

      {/* Onboarding banner — shown to institutional users who land via ?onboarding=1 */}
      {isInstitutional && (onboardingParam || onboardingIncomplete) && (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="p-4 flex items-start gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/15 text-emerald-600">
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="flex-1 text-xs space-y-1">
              <p className="text-sm font-semibold text-foreground">Finish setting up your firm</p>
              <p className="text-muted-foreground">
                Your SEBI registration and disclaimer will be embedded on every proposal, pitch report, and client
                communication generated through KubAIr Advisor OS. Fill these in once, they'll apply everywhere.
              </p>
              <ul className="space-y-0.5 mt-2">
                <li className="flex items-center gap-1.5">
                  {brandForm.registration_number ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <span className="w-3 h-3 rounded-full border border-muted-foreground/50" />}
                  <span>SEBI registration number</span>
                </li>
                <li className="flex items-center gap-1.5">
                  {brandForm.disclaimer_text ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <span className="w-3 h-3 rounded-full border border-muted-foreground/50" />}
                  <span>Default disclaimer</span>
                </li>
                <li className="flex items-center gap-1.5">
                  {brandForm.logo_url ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <span className="w-3 h-3 rounded-full border border-muted-foreground/50" />}
                  <span>Firm logo (optional)</span>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && (
        <div className="flex items-center gap-2 justify-center py-12 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading…</span>
        </div>
      )}

      {!loading && orgs.length === 0 && !showCreate && (
        <Card>
          <CardContent className="p-8 text-center space-y-3">
            <Building2 className="w-10 h-10 mx-auto text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">You haven't created or joined any organization yet.</p>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
              onClick={() => setShowCreate(true)}
            >
              <Building2 className="w-4 h-4" /> Create Organization
            </Button>
          </CardContent>
        </Card>
      )}

      {showCreate && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <h3 className="text-sm font-semibold">Create Organization</h3>
            <Input
              placeholder="Organization name (e.g. Acme Capital)"
              value={newOrgName}
              onChange={e => setNewOrgName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
            />
            <div className="flex gap-2">
              <Button onClick={handleCreate} disabled={creating || !newOrgName.trim()} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
                {creating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                Create
              </Button>
              <Button variant="ghost" className="text-xs" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {orgs.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {orgs.map(o => (
            <button
              key={o.id}
              onClick={() => loadOrgDetail(o.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                activeOrg?.id === o.id ? 'border-emerald-500 bg-emerald-500/5 text-emerald-600' : 'border-border hover:border-border/80'
              }`}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}

      {activeOrg && (
        <>
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">{activeOrg.name}</h3>
                <p className="text-[11px] text-muted-foreground">Slug: {activeOrg.slug} · Created {new Date(activeOrg.created_at).toLocaleDateString('en-IN')}</p>
              </div>
              {canManage && (
                <Button size="sm" className="text-xs h-7 gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setShowInvite(!showInvite)}>
                  <UserPlus className="w-3 h-3" /> Invite
                </Button>
              )}
            </div>

            {showInvite && (
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="text-[11px] text-muted-foreground mb-1 block">Email</label>
                  <Input placeholder="colleague@company.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} className="text-xs" />
                </div>
                <div>
                  <label className="text-[11px] text-muted-foreground mb-1 block">Role</label>
                  <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} className="h-9 px-2 rounded-md border border-border bg-background text-xs">
                    <option value="admin">Admin</option>
                    <option value="member">Member</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>
                <Button size="sm" className="text-xs h-9" disabled={inviting || !inviteEmail.trim()} onClick={handleInvite}>
                  {inviting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Send'}
                </Button>
              </div>
            )}

            <div className="space-y-1">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Members ({activeOrg.members?.length || 0})</h4>
              {(activeOrg.members || []).map(m => (
                <div key={m.id} className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-secondary/30 transition-colors">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center text-[11px] font-semibold">
                      {(m.email || m.user_id || '?')[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate">{m.email || m.user_id}</p>
                      <p className="text-[10px] text-muted-foreground">{m.status === 'pending' ? 'Invite pending' : 'Active'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {canManage && m.role !== 'owner' ? (
                      <select value={m.role} onChange={e => handleRoleChange(m.user_id, e.target.value)} className="text-[10px] px-1.5 py-0.5 rounded border border-border bg-background">
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    ) : (
                      <RoleBadge role={m.role} />
                    )}
                    {canManage && m.role !== 'owner' && (
                      <button onClick={() => handleRemove(m.user_id)} className="text-muted-foreground hover:text-red-500 transition-colors">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Branding + SEBI disclosure — critical for institutional compliance */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-sm font-semibold">Branding & SEBI disclosure</h3>
                <p className="text-xs text-muted-foreground">
                  These appear on every proposal, pitch report, and client export. KubAIr is a software vendor —
                  your firm's registration is what clients see.
                </p>
              </div>
              {canManage && (
                <Button size="sm" disabled={!brandDirty || brandSaving} onClick={saveBrand}
                  className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white h-8">
                  {brandSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  {brandSaved && !brandDirty ? 'Saved' : 'Save'}
                </Button>
              )}
            </div>

            <div className="grid gap-2 text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-[11px] text-muted-foreground">SEBI category</label>
                  <select value={brandForm.sebi_category} onChange={e => updateBrand({ sebi_category: e.target.value })} disabled={!canManage}
                    className="w-full h-9 rounded-md border border-border bg-background px-2 text-xs">
                    {SEBI_CATEGORIES.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] text-muted-foreground">SEBI registration number</label>
                  <Input placeholder="INA00000XXXX" value={brandForm.registration_number}
                    onChange={e => updateBrand({ registration_number: e.target.value.toUpperCase() })}
                    disabled={!canManage} className="text-xs uppercase" />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[11px] text-muted-foreground">Logo URL (square, &lt; 512px preferred)</label>
                <Input placeholder="https://firm.in/logo.png" value={brandForm.logo_url}
                  onChange={e => updateBrand({ logo_url: e.target.value })} disabled={!canManage} className="text-xs" />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] text-muted-foreground">Primary color (HEX)</label>
                <div className="flex gap-2 items-center">
                  <Input placeholder="#10b981" value={brandForm.primary_color}
                    onChange={e => updateBrand({ primary_color: e.target.value })} disabled={!canManage} className="text-xs w-32" />
                  <span className="inline-block w-6 h-6 rounded border border-border" style={{ background: brandForm.primary_color }} />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[11px] text-muted-foreground">Default disclaimer (appears on every PDF and exported memo)</label>
                <textarea rows={4} value={brandForm.disclaimer_text}
                  onChange={e => updateBrand({ disclaimer_text: e.target.value })}
                  disabled={!canManage}
                  className="w-full rounded-md border border-border bg-background p-2 text-xs"
                />
                {canManage && !brandForm.disclaimer_text && brandForm.registration_number && (
                  <button type="button"
                    onClick={() => updateBrand({
                      disclaimer_text: defaultDisclaimer(brandForm.registration_number, brandForm.sebi_category, activeOrg.name),
                    })}
                    className="text-[11px] text-emerald-600 hover:underline">
                    Generate default disclaimer from SEBI details
                  </button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-2">
            <h3 className="text-sm font-semibold">Activity</h3>
            <p className="text-xs text-muted-foreground">{audit.total} events</p>
            <div className="max-h-64 overflow-y-auto space-y-2 text-xs">
              {(audit.items || []).map((a) => (
                <div key={a.id} className="border-b border-border/40 pb-2">
                  <p className="font-medium">{a.action}</p>
                  <p className="text-muted-foreground">{a.user_name || a.user_id} · {a.created_at ? new Date(a.created_at).toLocaleString() : ''}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        </>
      )}

      {!loading && orgs.length > 0 && !showCreate && (
        <Button variant="outline" className="text-xs gap-1.5" onClick={() => setShowCreate(true)}>
          <Building2 className="w-3 h-3" /> New Organization
        </Button>
      )}
    </div>
  );
}
